import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { FormattedTime } from 'react-intl';
import { getTimeFromHour } from '../../../../../modules/datetime';
import { compose, pure } from '../../../../../src/web/utils/react';
import Translation from '../../../../components/stateless/Translation';

const propTypes = {
  hours: PropTypes.arrayOf(PropTypes.shape({
    day: PropTypes.string.isRequired,
    open: PropTypes.number.isRequired,
    close: PropTypes.number.isRequired,
  })),
  use24HourTime: PropTypes.bool.isRequired,
  timezone: PropTypes.string,
  useColors: PropTypes.bool, // New prop
};

const defaultProps = {
  hours: undefined,
  timezone: undefined,
  useColors: true, // Default value for the new prop
};

const daysOfTheWeek = {
  Sunday: { id: 'weekday.sun', defaultMessage: 'Sun' },
  Monday: { id: 'weekday.mon', defaultMessage: 'Mon' },
  Tuesday: { id: 'weekday.tue', defaultMessage: 'Tue' },
  Wednesday: { id: 'weekday.wed', defaultMessage: 'Wed' },
  Thursday: { id: 'weekday.thu', defaultMessage: 'Thurs' },
  Friday: { id: 'weekday.fri', defaultMessage: 'Fri' },
  Saturday: { id: 'weekday.sat', defaultMessage: 'Sat' },
};

function locationOpen(timezone, open, close) {
  // Check for closed state
  if (open === -1 || close === -1) {
    return false;
  }
  // Check for 24-hour operation (when open equals close)
  if (open === close) {
    return true;
  }
  const now = new Date();
  const options = { timeZone: timezone, hour: '2-digit', minute: '2-digit', hour12: false };
  const currentTimeString = now.toLocaleTimeString('en-US', options);
  const [hours, minutes] = currentTimeString.split(':').map(Number);
  const currentTime = Number((hours + minutes / 60).toFixed(2));

  return currentTime >= open && currentTime < close;
}

function rollUpHours(title, hours, use24HourTime, onlyToday, timezone, useColors) {
  const [first, ...rest] = hours;
  const formattedHours = [...rest, first];
  const today = Object.keys(daysOfTheWeek)[(new Date()).getDay()];
  const hoursForToday = formattedHours.filter(({ day }) => day === today);
  const hoursToShow = onlyToday ? hoursForToday : formattedHours;
  const closedAllDayOnlyToday = onlyToday ? 'All Day' : 'Closed';
  const closedAllDayOnlyTodayId = onlyToday ? 'allDay' : 'closed';
  const openAllDayOnlyToday = onlyToday ? 'All Day' : 'Open 24 Hours';
  const openAllDayOnlyTodayId = onlyToday ? 'allDay' : 'open24Hours';

  return hoursToShow.map(({ day, open, close }) => {
    const fontWeight = (day === today && !onlyToday) ? 'bold' : 'normal';
    const isOpen = locationOpen(timezone, open, close);

    let openHours;
    if (open === -1 || close === -1) {
      openHours = <span style={{ fontWeight }}><Translation id={`widgets.hours.hoursTable.${closedAllDayOnlyTodayId}`} defaultMessage={closedAllDayOnlyToday} /></span>;
    } else if (open === close) {
      openHours = <span style={{ fontWeight }}><Translation id={`widgets.hours.hoursTable.${openAllDayOnlyTodayId}`} defaultMessage={openAllDayOnlyToday} /></span>;
    } else if (isOpen) {
      openHours = (
        <span className="text-sm">
          <Translation id="widgets.hours.hoursTable.openNowToggle.Closes" defaultMessage="Closes at" />{' '}
          <span className="lowercase"><FormattedTime value={getTimeFromHour(close)} hour12={!use24HourTime} />
          </span>
        </span>
      );
    } else {
      openHours = (
        <span className="text-sm">
          <Translation id="widgets.hours.hoursTable.openNowToggle.Opens" defaultMessage="Opens at" />{' '}
          <span className="lowercase"><FormattedTime value={getTimeFromHour(open)} hour12={!use24HourTime} />
          </span>
        </span>
      );
    }

    const todayComponent = (isOpen) ? (
      <>
        <span className={useColors ? 'font-bold text-green-800' : ''}>
          <Translation id="widgets.hours.hoursTable.open" defaultMessage="Open" />
        </span>
        {' - '}
        {openHours}
      </>
    ) : (
      <>
        <span className={useColors ? 'font-bold text-sm-red' : ''}>
          <Translation id="widgets.hours.hoursTable.closed" defaultMessage="Closed" />
        </span>
        {' - '}
        {openHours}
      </>
    );
    const dayComponent = onlyToday && todayComponent;

    return {
      day: dayComponent,
      openHours,
    };
  });
}

const SMHoursTable = ({ title, hours, use24HourTime, timezone, useColors }) => {
  if (!hours || !hours.length) {
    return false;
  }

  const [onlyToday] = useState(true);
  const groups = useMemo(() => rollUpHours(title, hours, use24HourTime,
    onlyToday, timezone, useColors), [title, hours, use24HourTime, onlyToday, timezone, useColors]);

  return (
    <>
      {onlyToday && (
        groups.map(({ day }) => (
          <span key={day}>
            {day}
          </span>
        ))
      )}
    </>
  );
};

SMHoursTable.propTypes = propTypes;
SMHoursTable.defaultProps = defaultProps;

export default compose(
  pure(),
)(SMHoursTable);
